import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSnackbar } from "../SnackBar";
import ViewJobModal from "../../Containers/ViewJobModal";
import InviteModal from "../Modals/InviteModal";
import DeclineJobModal from "../../Containers/ViewJobModal/DeclineJobModal";
import CancelOrderModal from "../../Containers/ViewJobModal/CancelOrderModal";
import JobUnavailableModal from "../../Containers/ViewJobModal/JobUnavailableModal";
import { apiGetRequest, apiPostRequest, apiPutRequest } from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
import { AppContext } from "../../Context/AppContext";
import { useSocketContext } from "../../Context/SocketContext/SocketProvider";
import { DONE_SERVICE_STATUS_NAMES } from "../../Constants/serviceOrderStatus";
import useServiceStatusMap from "../../hooks/useServiceStatusMap";
import { Box, CircularProgress } from "@mui/material";
import { loaderBoxStyles } from "./styles";
import { useTranslation } from "react-i18next";
import { minimumFromRefusal } from "../../Constants/orderMinimum";

export const useJobModalsManager = ({ onJobActionComplete } = {}) => {
  const snackbar = useSnackbar();
  const { profileData } = useContext(AppContext);
  const { t } = useTranslation("view-job-modal");
  const [openViewJobModal, setOpenViewJobModal] = useState(false);
  const [openInviteModal, setOpenInviteModal] = useState(false);
  const [openDeclineDialog, setOpenDeclineDialog] = useState(false);
  const [openJobUnavailableDialog, setOpenJobUnavailableDialog] =
    useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  const [viewJobLoader, setViewJobLoader] = useState(false);
  const [approveJobLoader, setApproveJobLoader] = useState(false);
  const [requestPaymentLoader, setRequestPaymentLoader] = useState(false);
  const [isDeclineJobLoader, setIsDeclineJobLoader] = useState(false);
  const [isCancelJobLoader, setIsCancelJobLoader] = useState(false);
  const [returnJobLoader, setReturnJobLoader] = useState(false);
  const [advanceStatusLoader, setAdvanceStatusLoader] = useState(false);
  // COMY1-292 — the supplier "Edit Order" in-modal editor.
  const [editMode, setEditMode] = useState(false);
  const [updateOrderLoader, setUpdateOrderLoader] = useState(false);

  const [disabledRequestPaymentBtn, setDisabledRequestPaymentBtn] = useState(
    {},
  );

  const [selectedJobData, setSelectedJobData] = useState(null);
  const [selectedJobDataForInvite, setSelectedJobDataForInvite] =
    useState(null);
  const [selectedJobViewType, setSelectedJobViewType] = useState(null);

  // Identifies the job the modal currently shows, for the real-time refresh
  // below. `type` is the row's item type — the details endpoint needs it to
  // look a catalog order up in the right tables.
  const openJobRef = useRef(null);
  const editModeRef = useRef(false);
  useEffect(() => {
    editModeRef.current = editMode;
  }, [editMode]);

  const openJobModal = async (job, viewType) => {
    try {
      setViewJobLoader(true);
      const response = await apiGetRequest(
        // job.type ('product' | 'restaurant') switches the backend to that
        // vertical's order tables so the modal gets the right details.
        endpoints.getSupplierJobDetails(job.request_id, profileData.id, job.type),
      );
      const data = response.data;

      openJobRef.current = { jobId: job.request_id, type: job.type };
      setSelectedJobData(data);
      setSelectedJobViewType(viewType);
      setOpenViewJobModal(true);
    } catch (error) {
      console.error("Error opening job modal:", error.message);
    } finally {
      setViewJobLoader(false);
    }

    // setSelectedJobData(job);
    // setSelectedJobViewType("preview");
    // setOpenViewJobModal(true);
  };

  /**
   * Re-reads the open job and re-buckets the modal from the fresh status, so a
   * job paid while the supplier has it open turns into the completed view
   * (green tag, no Request payment / Cancel actions) instead of sitting there
   * as if it were still in progress (COMY1-334).
   */
  const refreshOpenJob = useCallback(async () => {
    const open = openJobRef.current;
    if (!open) return;
    try {
      const response = await apiGetRequest(
        endpoints.getSupplierJobDetails(open.jobId, profileData.id, open.type),
      );
      const data = response.data;
      setSelectedJobData(data);
      if (data?.status) setSelectedJobViewType(data.status);
    } catch (error) {
      console.error("Error refreshing job modal:", error.message);
    }
  }, [profileData]);

  // Real-time: the open job's order changed status — most visibly the member
  // paying it. Only the job on screen is re-read; the lists behind the modal
  // refresh through their own subscription.
  const { socket } = useSocketContext();
  // Status ids are DB rows; the payload carries ids, so translate them
  // through the fetched map rather than hardcoding any (COMY1-283).
  const serviceStatusMap = useServiceStatusMap();
  const serviceStatusMapRef = useRef(serviceStatusMap);
  useEffect(() => {
    serviceStatusMapRef.current = serviceStatusMap;
  }, [serviceStatusMap]);
  useEffect(() => {
    if (!socket) return;
    const handler = (payload) => {
      const open = openJobRef.current;
      if (!open) return;
      if (String(payload?.order_id) !== String(open.jobId)) return;
      // PAYMENT_DONE → CLOSED is the member's feedback survey: nothing on the
      // supplier's side changes, so leave the modal alone.
      const { nameById } = serviceStatusMapRef.current;
      if (
        DONE_SERVICE_STATUS_NAMES.includes(nameById[Number(payload?.old_status_id)]) &&
        DONE_SERVICE_STATUS_NAMES.includes(nameById[Number(payload?.new_status_id)])
      ) {
        return;
      }
      // Don't yank the data out from under a supplier mid-edit.
      if (editModeRef.current) return;
      refreshOpenJob();
    };
    const CHANNELS = [
      "service_order_status_update",
      "product_order_status_update",
      "restaurant_order_status_update",
    ];
    CHANNELS.forEach((ch) => socket.on(ch, handler));
    return () => CHANNELS.forEach((ch) => socket.off(ch, handler));
  }, [socket, refreshOpenJob]);

  const handleCloseViewJobModal = () => {
    setOpenViewJobModal(false);
    setEditMode(false);
    openJobRef.current = null;
  };

  // COMY1-292 — submit the edited order. The backend moves the order back to
  // member re-approval and notifies the client, so we close + refresh on success.
  const handleUpdateOrder = async (payload) => {
    try {
      setUpdateOrderLoader(true);
      await apiPutRequest(
        endpoints.updateSupplierJobOrder(
          selectedJobData.job_id,
          profileData.id,
        ),
        payload,
      );
      snackbar(t("alerts.order_updated"), "success");
      setEditMode(false);
      handleCloseViewJobModal();
      onJobActionComplete?.();
    } catch (error) {
      const refusedMinimum = minimumFromRefusal(error);
      if (refusedMinimum != null) {
        snackbar(t("below_minimum_refused", { amount: refusedMinimum }), "error");
        return;
      }
      // A 5xx comes back with an `error_id` the backend logged the real cause
      // under — show it so a supplier report ("it just says try again") can be
      // traced without guessing at timestamps.
      const errorId = error?.response?.data?.error_id;
      console.error(
        "Error updating order:",
        error?.response?.data?.message || error.message,
        errorId ? `(error_id: ${errorId})` : "",
      );
      snackbar(
        errorId
          ? `${t("alerts.order_update_failed")} (${errorId})`
          : t("alerts.order_update_failed"),
        "error",
      );
    } finally {
      setUpdateOrderLoader(false);
    }
  };

  // Store and restaurant orders behave identically here — both are catalog
  // orders paid up front, and the BE takes the vertical as `type`. Falling
  // through to the service path would hit the wrong endpoints entirely.
  const catalogJobType =
    selectedJobData?.type === "product" || selectedJobData?.type === "restaurant"
      ? selectedJobData.type
      : null;

  const handleApproveJob = async () => {
    // Catalog orders are already paid → accepting is a status change
    // (PENDING → CONFIRMED). No supplier-invite modal.
    if (catalogJobType) {
      try {
        setApproveJobLoader(true);
        await apiPostRequest(
          endpoints.supplierJobActions(
            selectedJobData.job_id,
            profileData.id,
            "approve",
          ),
          { type: catalogJobType },
        );
        handleCloseViewJobModal();
        onJobActionComplete?.();
      } catch (error) {
        console.error("Error approving catalog job:", error.message);
      } finally {
        setApproveJobLoader(false);
      }
      return;
    }

    try {
      setApproveJobLoader(true);
      const response = await apiPostRequest(
        endpoints.supplierJobActions(
          selectedJobData.job_id,
          profileData.id,
          "approve",
        ),
      );
      const data = response.data;

      if (data.status === "success") {
        setSelectedJobDataForInvite(data.client_contact);
        setOpenInviteModal(true);
        onJobActionComplete?.();
      } else {
        setSelectedJobDataForInvite(data);
        setOpenJobUnavailableDialog(true);
        onJobActionComplete?.();
      }
    } catch (error) {
      console.error("Error approving job:", error.message);
    } finally {
      setApproveJobLoader(false);
    }
  };

  const handleDeclineButtonClick = () => setOpenDeclineDialog(true);
  const handleCancelButtonClick = () => setOpenCancelDialog(true);

  // Advance a product order to the next status (the Job page footer button on
  // active product orders) — COMY1-283.
  const handleAdvanceProductStatus = async (statusId) => {
    try {
      setAdvanceStatusLoader(true);
      await apiPutRequest(
        endpoints.updateProductJobStatus(selectedJobData.job_id, profileData.id),
        // Without `type` the BE defaults to the products tables and 400s on a
        // restaurant order id.
        { status_id: statusId, type: catalogJobType },
      );
      handleCloseViewJobModal();
      onJobActionComplete?.();
    } catch (error) {
      console.error("Error advancing product status:", error.message);
    } finally {
      setAdvanceStatusLoader(false);
    }
  };

  // Supplier un-checked unavailable items → return the product order to the
  // member for re-approval (COMY1-283).
  const handleReturnProductForReapproval = async (
    unavailableItemIds,
    quantities = {},
  ) => {
    try {
      setReturnJobLoader(true);
      await apiPutRequest(
        endpoints.returnProductJobForReapproval(
          selectedJobData.job_id,
          profileData.id,
        ),
        {
          unavailable_item_ids: unavailableItemIds,
          // Lines the supplier cut to a smaller quantity than ordered. The
          // backend stores these as the items' available quantity and captures
          // the recalculated amount when the member approves.
          item_quantities: Object.entries(quantities).map(
            ([itemId, quantity]) => ({
              item_id: Number(itemId),
              quantity,
            }),
          ),
          type: catalogJobType,
        },
      );
      handleCloseViewJobModal();
      onJobActionComplete?.();
    } catch (error) {
      console.error("Error returning product order:", error.message);
      const refusedMinimum = minimumFromRefusal(error);
      if (refusedMinimum != null) {
        snackbar(t("below_minimum_refused", { amount: refusedMinimum }), "error");
      }
    } finally {
      setReturnJobLoader(false);
    }
  };

  const handleJobDecline = async () => {
    try {
      setIsDeclineJobLoader(true);
      await apiPostRequest(
        endpoints.supplierJobActions(
          selectedJobData.job_id,
          profileData.id,
          "decline",
        ),
        catalogJobType ? { type: catalogJobType } : undefined,
      );

      setIsDeclineJobLoader(false);
      setOpenDeclineDialog(false);
      handleCloseViewJobModal();
      onJobActionComplete?.();
    } catch (error) {
      console.error("Error declining job:", error.message);
    } finally {
      setIsDeclineJobLoader(false);
    }
  };

  const handleJobCancel = async (reason) => {
    try {
      setIsCancelJobLoader(true);

      // Catalog orders have no service "cancel" endpoint — canceling sets the
      // order status to CANCELED_BY_SUPPLIER via the decline action.
      if (catalogJobType) {
        await apiPostRequest(
          endpoints.supplierJobActions(
            selectedJobData.job_id,
            profileData.id,
            "decline",
          ),
          { type: catalogJobType },
        );
        setIsCancelJobLoader(false);
        setOpenCancelDialog(false);
        handleCloseViewJobModal();
        onJobActionComplete?.();
        return;
      }

      const requestBody = {
        reason: reason,
      };

      await apiPostRequest(
        endpoints.supplierJobActions(
          selectedJobData.job_id,
          profileData.id,
          "cancel",
        ),
        requestBody,
      );

      setIsCancelJobLoader(false);
      setOpenCancelDialog(false);
      handleCloseViewJobModal();
      onJobActionComplete?.();
    } catch (error) {
      console.error("Error canceling job:", error.message);
    } finally {
      setIsCancelJobLoader(false);
    }
  };

  const handleRequestPayment = async (jobId) => {
    try {
      setRequestPaymentLoader(true);
      await apiPostRequest(
        endpoints.supplierJobActions(
          selectedJobData.job_id,
          profileData.id,
          "request-payment",
        ),
      );
      snackbar(t("alerts.requested_payment"), "success");
      setDisabledRequestPaymentBtn((prev) => ({
        ...prev,
        [jobId]: Date.now(),
      }));
      handleCloseViewJobModal();

      setTimeout(
        () => {
          setDisabledRequestPaymentBtn((prev) => {
            const updated = { ...prev };
            delete updated[jobId];
            return updated;
          });
        },
        10 * 60 * 1000,
      );
    } catch (error) {
      console.error("Error requesting payment:", error.message);
      const refusedMinimum = minimumFromRefusal(error);
      if (refusedMinimum != null) {
        snackbar(t("below_minimum_order", { amount: refusedMinimum }), "error");
      }
    } finally {
      setRequestPaymentLoader(false);
    }
  };

  return {
    openJobModal,

    modalState: {
      openViewJobModal,
      openInviteModal,
      openDeclineDialog,
      openJobUnavailableDialog,
      openCancelDialog,
      viewJobLoader,
      approveJobLoader,
      requestPaymentLoader,
      isDeclineJobLoader,
      isCancelJobLoader,
      returnJobLoader,
      advanceStatusLoader,
      editMode,
      updateOrderLoader,
      disabledRequestPaymentBtn,
      selectedJobData,
      selectedJobViewType,
      selectedJobDataForInvite,
    },

    modalHandlers: {
      setEditMode,
      handleUpdateOrder,
      setOpenInviteModal,
      setOpenDeclineDialog,
      setOpenJobUnavailableDialog,
      setOpenCancelDialog,
      handleCloseViewJobModal,
      handleApproveJob,
      handleDeclineButtonClick,
      handleCancelButtonClick,
      handleJobDecline,
      handleJobCancel,
      handleRequestPayment,
      handleReturnProductForReapproval,
      handleAdvanceProductStatus,
    },
  };
};

export const JobModalsManager = ({ modalState, modalHandlers }) => {
  const {
    openViewJobModal,
    openInviteModal,
    openDeclineDialog,
    openJobUnavailableDialog,
    openCancelDialog,
    viewJobLoader,
    approveJobLoader,
    requestPaymentLoader,
    isDeclineJobLoader,
    isCancelJobLoader,
    returnJobLoader,
    advanceStatusLoader,
    editMode,
    updateOrderLoader,
    disabledRequestPaymentBtn,
    selectedJobData,
    selectedJobViewType,
    selectedJobDataForInvite,
  } = modalState;

  const {
    setOpenInviteModal,
    handleCloseViewJobModal,
    handleApproveJob,
    handleDeclineButtonClick,
    handleCancelButtonClick,
    handleJobDecline,
    handleJobCancel,
    handleRequestPayment,
    handleReturnProductForReapproval,
    handleAdvanceProductStatus,
    setEditMode,
    handleUpdateOrder,
  } = modalHandlers;

  const { t: tInvite } = useTranslation("invite-modal");

  return (
    <>
      <ViewJobModal
        viewType={selectedJobViewType}
        openViewJobModal={openViewJobModal}
        handleCloseViewJobModal={handleCloseViewJobModal}
        jobData={selectedJobData}
        handleDeclineButtonClick={handleDeclineButtonClick}
        handleApproveJob={handleApproveJob}
        actionButtonLoader={
          selectedJobViewType === "pending"
            ? approveJobLoader
            : requestPaymentLoader
        }
        handleCancelButtonClick={handleCancelButtonClick}
        handleRequestPayment={() =>
          handleRequestPayment(selectedJobData?.job_id)
        }
        disabledRequestPaymentBtn={disabledRequestPaymentBtn}
        handleReturnProductForReapproval={handleReturnProductForReapproval}
        returnJobLoader={returnJobLoader}
        handleAdvanceProductStatus={handleAdvanceProductStatus}
        advanceStatusLoader={advanceStatusLoader}
        editMode={editMode}
        setEditMode={setEditMode}
        handleUpdateOrder={handleUpdateOrder}
        updateOrderLoader={updateOrderLoader}
      />

      <InviteModal
        openSuccess={openInviteModal}
        handleClose={() => {
          setOpenInviteModal(false);
          handleCloseViewJobModal();
        }}
        email={selectedJobDataForInvite?.contact_options?.email}
        phoneNumber={selectedJobDataForInvite?.contact_options?.phone}
        whatsappNumber={selectedJobDataForInvite?.contact_options?.phone}
        name={selectedJobDataForInvite?.client?.name}
        clientName={selectedJobDataForInvite?.client?.name}
        clientLogo={selectedJobDataForInvite?.client?.image_url}
        title={tInvite("got_job_title")}
        successMessage={tInvite("got_job_success_message")}
      />

      <DeclineJobModal
        open={openDeclineDialog}
        onClose={() => modalHandlers.setOpenDeclineDialog(false)}
        onActionClick={handleJobDecline}
        isLoading={isDeclineJobLoader}
      />

      <CancelOrderModal
        open={openCancelDialog}
        onClose={() => modalHandlers.setOpenCancelDialog(false)}
        onActionClick={handleJobCancel}
        isLoading={isCancelJobLoader}
      />

      <JobUnavailableModal
        open={openJobUnavailableDialog}
        onClose={() => {
          modalHandlers.setOpenJobUnavailableDialog(false);
          handleCloseViewJobModal();
        }}
        onActionClick={() => {
          modalHandlers.setOpenJobUnavailableDialog(false);
          handleCloseViewJobModal();
        }}
        job_unavailable_reason={selectedJobDataForInvite?.reason}
      />

      {viewJobLoader && (
        <Box sx={loaderBoxStyles}>
          <CircularProgress
            size={80}
            sx={{
              color: "#248B70",
            }}
          />
        </Box>
      )}
    </>
  );
};
